import {AfterViewInit, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {AnimationService, AnimationType} from '@core/services/animation.service';
import {VotingControlService} from '@modules/voting/voting-control.service';
import {SectionItem} from '@modules/voting/models/section-item';
import {ITEM_TYPE_MAP} from '@modules/voting/models/voting';
import {ItemType, VotingDisplayMode} from '@modules/voting/models/enums';
import {Subscription} from 'rxjs';


@Component({
  selector: 'app-voting-section',
  templateUrl: './voting-section.component.html',
  styleUrls: ['./voting-section.component.scss'],
})
export class VotingSectionComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  readonly SEARCHBAR_VISIBILITY_ITEM_THRESHOLD = 25;

  @Input() title;
  @Input() selectionLimit;
  @Input() icon;
  @Input() items: SectionItem[];
  @ViewChild('selectionInfo') selectionInfo;
  public adItemIndex: number;
  public nrSelectedItems = 0;
  public selectionValid = false;
  selectionInfoText = '';
  showSearchFilter = false;
  itemsType = ItemType.UNKNOWN;
  _selectedItems: SectionItem[] = []; // store selected items in order of selection
  displayedItems: SectionItem[] = [];
  displayModeChangeSubscription: Subscription = null;

  constructor(
    private animationService: AnimationService,
    public votingControl: VotingControlService
  ) { }

  ngOnInit(): void {
    this.displayModeChangeSubscription = this.votingControl.onDisplayModeChange.subscribe(mode => {
      this.applyResultDisplaySettings();
      this.updateSearchBarVisibility();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.applyResultDisplaySettings();
  }

  ngAfterViewInit (): void {
    this.displayedItems = this.items;
    this.updateSearchBarVisibility();
    const firstType = this.items[0]?.type;
    const allItemsHaveSameType = this.items.every(item => {
      return item.type === firstType;
    });
    if (allItemsHaveSameType) {
      this.itemsType = Object.values(ItemType).includes(firstType) ? firstType : ItemType.UNKNOWN;
    }
    this.applyResultDisplaySettings();
    this.updateSelectionInfoText();
    this.setAdItemIndex();
  }

  ngOnDestroy(): void {
    this.displayModeChangeSubscription.unsubscribe();
  }

  onSelectionChanged(item: SectionItem, value: boolean) {
    this._selectedItems.push(item);

    if (value === true) {
      if (this._selectedItems.length > this.selectionLimit.max) {
        const oldestSelectedItem = this._selectedItems.shift();

        if (oldestSelectedItem) {
          oldestSelectedItem.selected = false;
        }

        const options = this.animationService.prepareAnimationParams({background: '#ffff91', color: '#999'});
        this.animationService.animateElement(this.selectionInfo, AnimationType.CHANGE_COLOR_TEMP, options);
      }
    } else {
      this._selectedItems = this._selectedItems.filter(_item => _item !== item);
    }

    this.nrSelectedItems = this._selectedItems.length;
    this.selectionValid = this.validateSelection();
    this.updateSelectionInfoText();
  }

  updateSearchBarVisibility() {
    this.showSearchFilter = this.items.length > this.SEARCHBAR_VISIBILITY_ITEM_THRESHOLD && this.votingControl.submissionMode;
  }

  updateSelectionInfoText() {
    const itemStrings = ITEM_TYPE_MAP[this.itemsType];

    if (this.nrSelectedItems === 0) {
      const itemDescription = this.selectionLimit.max > 1 ? itemStrings.plural : itemStrings.singular;
      this.selectionInfoText = `Wähle max. ${this.selectionLimit.max} ${itemDescription}`;
    } else {
      const itemDescription = this.nrSelectedItems > 1 ? itemStrings.plural : itemStrings.singular;
      this.selectionInfoText = `${this.nrSelectedItems}/${this.selectionLimit.max} ${itemDescription} ausgewählt`;
    }
  }

  get selectedItems(): SectionItem[] {
    return this.items.filter(item => item.selected === true);
  }

  validateSelection() {
    return this.nrSelectedItems > this.selectionLimit.min && this.nrSelectedItems <= this.selectionLimit.max;
  }

  filterItemsByString(filterValue: string) {
    const comparableString = (input: string) => input?.toLowerCase().replace(/\/s|_|-|\/|\./gi, '');
    this.displayedItems = this.items.filter(item => comparableString(item.title)?.includes(comparableString(filterValue)));

    if (!filterValue) {
      this.applyResultDisplaySettings();
    }
  }

  private setAdItemIndex(): void {
    if (this.displayedItems?.length > 1) {
      this.adItemIndex = 1;
    }
    if (this.displayedItems?.length > 2) {
      this.adItemIndex = 2;
    }
  }

  private applyResultDisplaySettings() {
    if (this.votingControl.displayMode !== VotingDisplayMode.RESULTS) {
      this.displayedItems = this.items;
      return;
    }

    const itemsCopy = [...this.items];
    this.displayedItems = itemsCopy.sort(VotingControlService.SORT_ITEMS_BY_VOTES);

    if (!this.votingControl.resultDisplayConfig.showOnlyTopResults) {
      return;
    }

    this.displayedItems = this.displayedItems.slice(0, this.votingControl.resultDisplayConfig.nrOfVisibleItems);

    const lastVoteData = this.votingControl.getLastVoteData();

    if (lastVoteData) {
      lastVoteData.itemids.forEach(itemId => {
        const findItem = (items: SectionItem[]) => items.find(item => item.itemid === itemId);
        const displayedItem = findItem(this.displayedItems);
        if (!displayedItem) {
          this.displayedItems.push(findItem(this.items));
        }
      });
    }
  }
}
